import React, { useContext, useRef, useState } from 'react'
import { Modal, View } from 'oio-react'
import PropTypes from 'prop-types'
import * as Sentry from '@sentry/browser'
import CloseIcon from 'assets/icons/close'
import AsyncStripeProvider from 'src/components/utils/AsyncStripeProvider'
import { InitiativeHierarchyContext } from 'src/libraries/contexts/InitiativeHierarchy'
import { useJoinRole, useMe } from 'src/graphql/hooks'
import JoinError from './Error'
import JoinLogin from './Login'
import JoinWelcome from './Welcome'
import JoinRegistrationForm from './RegistrationForm'
import JoinPaymentForm from './PaymentForm'
import JoinSuccess from './Success'

// Join
const LOGIN_STEP = 'LOGIN_STEP'
const WELCOME_STEP = 'WELCOME_STEP'
const REGISTRATION_FORM_STEP = 'REGISTRATION_FORM_STEP'
const ADMISSIONS_COMMERCE_STEP = 'ADMISSIONS_COMMERCE_STEP'
const SUCCESS_STEP = 'SUCCESS_STEP'

// TODO: This modal doesn't animate out on exit currently
const InitiativeJoin = ({ onClose }) => {
   const { initiative, refetchInitiative } = useContext(InitiativeHierarchyContext)
   const { isLoggedIn, loading: userIsLoading } = useMe()
   const { joinRole } = useJoinRole()

   const [error, setError] = useState()
   const [currentStepIndex, setCurrentStepIndex] = useState(0)

   // Would be annoying to use state for registrationFormValues
   // because it may not be set before `register` fn is called.
   // Plus, changes to this do not require a re-render, since
   // this is only required when going back and forward between steps,
   // which is controlled by state changes anyway.
   const registrationFormValues = useRef({})
   const paymentFormValues = useRef({})

   // eslint-disable-next-line camelcase
   const registrationFormEnabled = initiative.apps?.registration_form?.enabled
   const admissionsCommerceEnabled = initiative.commerce.enabled

   const applicableSteps = [
      !isLoggedIn ? LOGIN_STEP : null,
      WELCOME_STEP,
      registrationFormEnabled ? REGISTRATION_FORM_STEP : null,
      admissionsCommerceEnabled ? ADMISSIONS_COMMERCE_STEP : null,
      SUCCESS_STEP
   ].filter(s => s !== null)

   const currentStep = applicableSteps[currentStepIndex]
   // We don't do -1 because we don't consider the WELCOME_STEP to be a human-readable step
   const currentStepNumber = currentStepIndex
   // We do -2 here because we don't consider WELCOME_STEP and SUCCESS_STEP as human-readable steps
   const numTotalSteps = applicableSteps.length - 2

   const currentUserIsAlreadyMember = currentStepIndex === 0 &&
      (initiative.currentUserRelationship?.elements.find(element => (
         element.__typename === 'RelationshipElementRoleParticipant' &&
         element.role.type === 'primary' &&
         element.status === 'active'
      )))

   if (userIsLoading) {
      return null
   }

   const register = async () => {
      try {
         const registrationFormQuestionIds = Object.keys(registrationFormValues.current)
         await joinRole({
            entityType: 'initiative',
            entityId: initiative.id,
            roleId: initiative.roles.find(r => r.type === 'primary').id,
            admissionsCommerceData: {
               billingEmail: paymentFormValues.current?.billingEmail,
               billingName: paymentFormValues.current?.billingName,
               paymentTokenId: paymentFormValues.current?.paymentTokenId,
               productIds: paymentFormValues.current?.products
                  ? [paymentFormValues.current.products]
                  : []
            },
            registrationFormData: registrationFormQuestionIds.map(questionId => ({
               elementId: questionId,
               value: registrationFormValues.current[questionId]
            }))
         })

         refetchInitiative()
         setCurrentStepIndex(applicableSteps.length - 1)
      } catch (err) {
         // console.log(err)
         Sentry.captureException(err)
         setError(err)
      }
   }

   const handleGoBack = () => {
      setCurrentStepIndex(stepIndex => stepIndex - 1)
   }

   const handleContinue = async () => {
      // If is last step
      if (currentStepNumber === numTotalSteps) {
         await register()
      } else {
         setCurrentStepIndex(stepIndex => stepIndex + 1)
      }
   }

   const handleSaveRegistrationFormAndGoBack = (values) => {
      registrationFormValues.current = values
      handleGoBack()
   }

   const handleSaveRegistrationFormAndContinue = async (values) => {
      registrationFormValues.current = values
      await handleContinue()
   }

   const handleSavePaymentFormAndGoBack = (values) => {
      paymentFormValues.current = values
      handleGoBack()
   }

   const handleSavePaymentFormAndContinue = async (values) => {
      paymentFormValues.current = values
      await handleContinue()
   }

   return (
      <Modal
         onCloseTrigger={onClose}
         overlayBackgroundColor="rgba(0,0,0,0.9)"
         borderRadius="6px[b-f]"
         width="100%[a] 90%[b-c] 80%[c] 540px[d-f]"
         height="100%[a] 90%[b-c] 85%[c-f]"
         open>
         <View
            position="absolute"
            top="0px"
            right="0px"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="48px"
            height="48px"
            onClick={onClose}
            zIndex="2">
            <CloseIcon width="24px" height="24px" />
         </View>
         <AsyncStripeProvider>
            <View
               borderRadius="9px[b-f]"
               style={{ overflow: 'hidden' }}
               float="left"
               width="100%"
               height="100%">
               {error && (
                  <JoinError
                     error={error}
                     onGoBack={() => setError(null)}
                  />
               )}
               {currentUserIsAlreadyMember && (
                  <JoinSuccess
                     title="You're Already Registered!"
                     message={`Looks like you are already ${initiative.class === 'event' ? 'registered for' : 'joined'} ${initiative.name}.`}
                  />
               )}
               {!error && !currentUserIsAlreadyMember && (
                  <>
                     {currentStep === LOGIN_STEP && (
                        <JoinLogin onContinue={handleContinue} />
                     )}
                     {currentStep === WELCOME_STEP && (
                        <JoinWelcome
                           onContinue={handleContinue}
                           onGoBack={handleGoBack}
                        />
                     )}
                     {currentStep === REGISTRATION_FORM_STEP && (
                        <JoinRegistrationForm
                           currentStepNumber={currentStepNumber}
                           initialRegistrationFormValues={registrationFormValues.current}
                           onContinue={handleSaveRegistrationFormAndContinue}
                           onGoBack={handleSaveRegistrationFormAndGoBack}
                           numTotalSteps={numTotalSteps}
                        />
                     )}
                     {currentStep === ADMISSIONS_COMMERCE_STEP && (
                        <JoinPaymentForm
                           currentStepNumber={currentStepNumber}
                           initialPaymentFormValues={paymentFormValues.current}
                           onContinue={handleSavePaymentFormAndContinue}
                           onGoBack={handleSavePaymentFormAndGoBack}
                           numTotalSteps={numTotalSteps}
                        />
                     )}
                     {currentStep === SUCCESS_STEP && <JoinSuccess />}
                  </>
               )}
            </View>
         </AsyncStripeProvider>
      </Modal>
   )
}

InitiativeJoin.propTypes = {
   onClose: PropTypes.func.isRequired
}

export default InitiativeJoin
